import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
import './assets/tailwind.css';

// Plugins
import VueScrollactive from 'vue-scrollactive';
import VModal from 'vue-js-modal';
import Vuelidate from 'vuelidate';
import Notifications from 'vue-notification';
import VueSnip from 'vue-snip';
import VueParallaxJs from 'vue-parallax-js';
import Vue2TouchEvents from 'vue2-touch-events';
import PortalVue from 'portal-vue';
import { createHead } from '@unhead/vue';
import { UnheadPlugin } from '@unhead/vue/vue2';

// Utils
import discountCode from './util/discountCode';
import { setupViteErrorHandler } from './helpers/viteErrorHandler';

// Custom plugins
import Growthbook from './plugins/growthbook';
import { i18n } from './plugins/i18n';
import './plugins/dayjs';
import AxiosPlugin from './plugins/axios';

// Globally register all `base`-prefixed components
import './components/_globals';

/**
 * Bootstrap the Vue application with common configuration
 * @param {Object} sentryConfig - Sentry configuration object (optional)
 * @returns {Vue} The Vue instance
 */
export function bootstrapApp(sentryConfig = null) {
    // Init filters (do this early as they're simple)
    Vue.filter('discountCode', discountCode);

    const head = createHead();

    // Initialize notifications first since axios error handling uses it
    Vue.use(Notifications);

    // Initialize Axios with Sentry reference (must happen before we use $axiosRetry)
    // Note: Sentry will be passed in after initialization if provided
    Vue.use(AxiosPlugin, {
        Sentry: sentryConfig?.Sentry,
        store,
        baseURL: import.meta.env.VITE_API_URL,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });

    // Init other UI plugins
    Vue.use(UnheadPlugin);
    Vue.use(VModal, { dynamic: true, injectModalsContainer: true });
    Vue.use(VueScrollactive);
    Vue.use(Growthbook);
    Vue.use(Vuelidate);
    Vue.use(VueSnip);
    Vue.use(VueParallaxJs);
    Vue.use(Vue2TouchEvents, {
        disableClick: true
    });
    Vue.use(PortalVue);

    // Initialize Sentry if configuration is provided
    if (
        sentryConfig &&
        import.meta.env.PROD &&
        import.meta.env.VITE_SENTRY_DSN
    ) {
        const Sentry = sentryConfig.Sentry;
        Sentry.init({
            Vue,
            dsn: import.meta.env.VITE_SENTRY_DSN,
            attachProps: true,
            logErrors: true,
            integrations: [
                Sentry.browserTracingIntegration({ router }),
                Sentry.httpClientIntegration({
                    failedRequestStatusCodes: [[401, 599]]
                }),
                Sentry.extraErrorDataIntegration()
            ],
            denyUrls: [
                /^safari-extension:\/\//i,
                /^safari-web-extension:\/\//i,
                /^moz-extension:\/\//i,
                /^chrome-extension:\/\//i,
                /moz-extension/i
            ],
            ignoreErrors: [
                '@webkit-masked-url',
                /no_open_bills/,
                'CanceledError'
            ],
            // Allow custom trace sample rate from config
            tracesSampleRate: sentryConfig.tracesSampleRate || 0.1
        });
    }

    Vue.config.productionTip = false;

    // Initialize Vue instance
    const app = new Vue({
        router,
        store,
        i18n,
        unhead: head,
        render: h => h(App)
    });

    // Global chunk error handling with cache busting and retry logic
    setupViteErrorHandler();

    return app;
}
